import { Component, EventEmitter, Injector, OnInit, Output } from "@angular/core";
import { AppComponentBase } from "@shared/app-component-base";
import { DuracaoConsultaModel } from "@shared/model/duracao-consulta.model";
import { AgendaDto, AgendaServiceProxy, EssentialProfisisonalSaudeDto, GuidEntityDto, ProfissionalSaudeServiceProxy, TipoSala, UpdateAgendaDto } from "@shared/service-proxies/service-proxies";
import { BsModalRef } from 'ngx-bootstrap/modal';
import { finalize } from 'rxjs/operators';

@Component({
    templateUrl: './edit-agenda.component.html',
    styleUrls: ['./edit-agenda.component.scss'],
})
export class EditAgendaComponent extends AppComponentBase implements OnInit {
    protected title: string = "Criar nova sala";
    protected subtitle: string = "Crie uma nova sala para gerenciar agendamentos";
    protected saving: boolean = false;
    protected profissionais: EssentialProfisisonalSaudeDto[] = [];
    protected profissionaisSelecionados: EssentialProfisisonalSaudeDto[] = [];
    protected profissionaisFiltrados: EssentialProfisisonalSaudeDto[] = [];
    protected agenda: UpdateAgendaDto = new UpdateAgendaDto();
    protected nome: string = '';
    protected id: string;
    protected suspenso: boolean = false;
    public tempoConsultas = DuracaoConsultaModel;
    protected TipoSala = TipoSala;

    @Output() onSave = new EventEmitter<any>();
    @Output() onDelete = new EventEmitter<any>();
    @Output() onSuspensaoAlterada = new EventEmitter<boolean>();

    constructor(
        injector: Injector,
        private _agendaService: AgendaServiceProxy,
        private _profissionalAppService: ProfissionalSaudeServiceProxy,
        public bsModalRef: BsModalRef
    ) {
        super(injector)
    }

    ngOnInit(): void {
        if (this.appSession.clinicaId == null) {
            this.getProfissional().then(() => {
                if (this.id) {
                    this.getAgenda(this.id);
                }
            });;
        } else {
            this.getProfissionais().then(() => {
                if (this.id) {
                    this.getAgenda(this.id);
                }
            });
        }
    }

    protected atualizarLista(nome: string) {
        if (!nome) {
            this.profissionaisFiltrados = [];
        } else {
            this.profissionaisFiltrados = this.profissionais
                .filter(x => x.nome.toLowerCase().includes(nome.toLowerCase()))
                .filter(x => this.profissionaisSelecionados.every(y => y.id !== x.id));
        }
    }

    protected insertProfissional(profissional: EssentialProfisisonalSaudeDto): void {
        if (this.profissionaisSelecionados.find(x => x.id == profissional.id)) return;

        this.profissionaisSelecionados.push(profissional);

        this.profissionaisFiltrados = [];
        this.nome = '';
    }

    protected removeProfissional(profissional: EssentialProfisisonalSaudeDto): void {
        this.profissionaisSelecionados = this.profissionaisSelecionados.filter(x => x.id != profissional.id);
    }

    protected alternarSuspensao(): void {
        const acao = this.suspenso ? 'remover a suspensão' : 'suspender';
        abp.message.confirm(
            `Deseja ${acao} da sala "${this.agenda.nomeSala}"?`,
            this.suspenso ? 'Remover Suspensão' : 'Suspender Sala',
            (result: boolean) => {
                if (result) {
                    abp.ui.setBusy();
                    this._agendaService.alternarSuspensao(new GuidEntityDto({ id: this.agenda.id }))
                        .pipe(finalize(() => abp.ui.clearBusy()))
                        .subscribe(() => {
                            const novoEstado = !this.suspenso;
                            this.suspenso = novoEstado;
                            this.onSuspensaoAlterada.emit(novoEstado);
                            abp.notify.success(novoEstado ? 'Sala suspensa com sucesso!' : 'Suspensão removida com sucesso!');
                        });
                }
            }
        );
    }

    protected excluirAgenda(): void {
        abp.message.confirm(
            `Deseja excluir a sala "${this.agenda.nomeSala}"? Esta ação também removerá todos os eventos associados.`,
            'Excluir Sala',
            (result: boolean) => {
                if (result) {
                    abp.ui.setBusy();
                    this._agendaService.delete(this.agenda.id)
                        .pipe(finalize(() => abp.ui.clearBusy()))
                        .subscribe(() => {
                            abp.notify.success('Sala excluída com sucesso!');
                            this.onDelete.emit();
                            this.bsModalRef.hide();
                        });
                }
            }
        );
    }

    protected save(): void {
        if (!this.agenda.duracaoConsulta) {
            abp.notify.warn("Informe a duração das consultas");
        }
        this.saving = true;
        this.agenda.profissionaisIds = this.profissionaisSelecionados.map(i => i.id);

        abp.ui.setBusy();
        this._agendaService.update(this.agenda)
            .pipe(
                finalize(() => {
                    abp.ui.clearBusy();
                    this.saving = false;
                })
            )
            .subscribe(() => {
                this.onSave.emit();
                this.bsModalRef.hide();
            })
    }

    private getAgenda(id: string): void {
        abp.ui.setBusy();
        this._agendaService.get(id)
            .pipe(
                finalize(() => {
                    abp.ui.clearBusy();
                })
            )
            .subscribe((agenda: AgendaDto) => {
                this.agenda = Object.assign(new UpdateAgendaDto(), agenda);
                this.suspenso = agenda.suspenso;
                this.profissionaisSelecionados = agenda.profissionais.map(ap => this.profissionais.find(p => p.id === ap.profissionalSaudeId));
            });
    }

    private getProfissionais(): Promise<void> {
        return new Promise((resolve, reject) => {
            abp.ui.setBusy();
            this._profissionalAppService.getProfissionaisByClinica(this.appSession.clinicaId).pipe(
                finalize(() => {
                    abp.ui.clearBusy();
                })
            ).subscribe((result) => {
                this.profissionais = result;
                resolve();
            }, (error) => {
                reject(error);
            });
        });
    }

    private getProfissional(): Promise<void> {
        return new Promise((resolve, reject) => {
            abp.ui.setBusy();
            this._profissionalAppService.get(this.appSession.profissionalId).pipe(
                finalize(() => {
                    abp.ui.clearBusy();
                })
            ).subscribe((result) => {
                let profissional = new EssentialProfisisonalSaudeDto();
                profissional.cpf = result.cpf;
                profissional.id = result.id;
                profissional.nome = result.nome
                this.insertProfissional(profissional);
                this.profissionais.push(profissional);
                resolve();
            }, (error) => {
                reject(error);
            });
        });
    }

}
